import { FilterValue } from "../../lib/filter";
import { ItemId, Serializer } from "./index";
import { AnswerType } from "../../lib/userdata";

export interface ListItem<I extends ItemId> {
	id: I;
	name: string;
	answer?: AnswerType;
	flagged?: boolean;
	initial?: number;
	[key: string]: any;
}

export class ListItem<I extends ItemId> {
	constructor(item: { id: string } & ListItem<unknown>, serializer: Serializer<I>) {
		return {
			...item,
			id: serializer.parse(item.id),
		};
	};
}

export class ListDataSource<I extends ItemId> {
	itemIdSerializer: Serializer<I>;

	constructor(item: Serializer<I>) {
		this.itemIdSerializer = item;
	}

	getItemList(): Promise<ListItem<I>[]> {
		return new Promise(resolve => resolve(
			Array.from(
				document.querySelectorAll<HTMLElement>('.item-list .item')
			).map((element: HTMLElement) => this.makeListItem(element.dataset))
		));
	}

	makeListItem(dataset: DOMStringMap): ListItem<I> {
		let answer;
		try {
			answer = JSON.parse(dataset.answer);
		} catch {
			answer = dataset.answer;
		}
		return new ListItem<I>({
			...Object.assign({}, dataset),
			id: dataset.id,
			name: dataset.name,
			answer,
			flagged: dataset.flagged?.toLowerCase() == 'true',
			initial: parseInt(dataset.initial)
		}, this.itemIdSerializer);
	}

	getFilteredItems(
		items: ListItem<I>[],
		filters: FilterValue[],
	): Promise<ListItem<I>[]> {
		const values: Record<string, {
			type: string, title: string, key: string, values: Set<string | boolean>
		}> = filters.reduce((result, filter) => {
			if (!(filter.key in result)) {
				result[filter.key] = {
					type: filter.type,
					title: filter.title,
					key: filter.key,
					values: new Set<string | boolean>()
				};
			}
			result[filter.key].values.add(filter.value);
			return result;
		}, {});
		// TODO: Determine if type='flag' is necessary, or if it can be handled by type='match'
		return new Promise(resolve => resolve(
			items.filter(item => Object.keys(values).every(key =>
				(values[key].type==='flag' && (
					(values[key].values.has(false) && (
						!(values[key].key in item) ||
						!item[values[key].key]
					)) ||
					(values[key].values.has(true) &&
						values[key].key in item &&
						item[values[key].key]
					)
				)) ||
				(values[key].type==='match' && (
					values[key].key in item &&
					values[key].values.has(item[values[key].key])
				))
			))
		));
	}

	setAttributes(id: I, data: Partial<ListItem<I>>): void {
		const item: HTMLElement = document.querySelector(
			`.item-list .item[data-id='${CSS.escape(this.itemIdSerializer.toString(id))}']`
		);
		for (const prop in data) {
			item.dataset[prop] = typeof data[prop] === 'object' ? JSON.stringify(data[prop]) : data[prop].toString();
		}
	}
}

export class NullListDataSource<I extends ItemId> extends ListDataSource<I> {
}